import styled from "@emotion/styled";
import {
  Button,
  Card as MuiCard,
  Grid,
  IconButton,
  Modal,
  Typography,
  useMediaQuery,
  Drawer,
  FormControlLabel,
  Switch,
} from "@mui/material";
import React, { useContext, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ReactComponent as MailIcon } from "./mail.svg";
import { ReactComponent as PhoneIcon } from "./phone.svg";
import { ReactComponent as MenuIcon } from "./menu.svg";
import { ReactComponent as LinkedIcon } from "./linked.svg";
import { ReactComponent as InstaIcon } from "./inst.svg";
import { AppContext } from "./App";
import gsap from "gsap";

const Card = styled(MuiCard)`
  color: #002b5b;
  height: 150px;
  width: 150px;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: all 0.3s;
  border: solid 1px;
  boxshadow: none;
  &:hover {
    cursor: pointer;
    background: #002b5b11;
    box-shadow: 0px 0px 10px 1px #002b5b;
  }
`;

const NAV_ITEMS = [
  { label: "About", id: "about" },
  { label: "Experience", id: "experience" },
  { label: "Projects", id: "projects" },
  { label: "Skills", id: "skill" },
  { label: "Education", id: "education" },
];

const Navbar = () => {
  const [activeSection, setActiveSection] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [openDrawer, setOpenDrawer] = useState(false);
  const smallScreen = useMediaQuery(`(min-width:800px)`);
  const { setDarkMode, darkMode, loaded } = useContext(AppContext);

  const navRef = useRef(null);
  const progressRef = useRef(null);
  const logoRef = useRef(null);
  const linksRef = useRef(null);
  const ctaRef = useRef(null);

  // ── Active section via IntersectionObserver ──────────────────────────────
  useEffect(() => {
    const observers = [];
    NAV_ITEMS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (!el) return;
      const observer = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) setActiveSection(id); },
        { threshold: 0.25 }
      );
      observer.observe(el);
      observers.push(observer);
    });
    return () => observers.forEach((o) => o.disconnect());
  }, []);

  // ── Scroll: hide/show + progress bar ────────────────────────────────────
  useEffect(() => {
    let lastScrollY = 0;
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const currentY = window.scrollY;
          const progress = progressRef.current;
          if (progress) {
            const total = document.documentElement.scrollHeight - window.innerHeight;
            progress.style.width = `${(currentY / total) * 100}%`;
          }
          if (navRef.current) {
            if (currentY > lastScrollY && currentY > 80) {
              gsap.to(navRef.current, { y: "-100%", duration: 0.35, ease: "power2.out" });
            } else {
              gsap.to(navRef.current, { y: "0%", duration: 0.35, ease: "power2.out" });
            }
          }
          lastScrollY = currentY;
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // ── Entrance animation after loader exits ────────────────────────────────
  useEffect(() => {
    if (!loaded) return;
    const els = [logoRef.current, linksRef.current, ctaRef.current].filter(Boolean);
    gsap.fromTo(els,
      { autoAlpha: 0, y: -22 },
      { autoAlpha: 1, y: 0, duration: 0.6, stagger: 0.1, ease: "power3.out", delay: 0.15 }
    );
  }, [loaded]);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <Grid
        ref={navRef}
        container
        height="72px"
        px={3}
        style={{
          background: "transparent",
          borderBottom: "1px solid rgba(21,152,149,0.15)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backdropFilter: "blur(28px)",
          position: "sticky",
          top: 0,
          width: "100%",
          zIndex: 9999,
          overflow: "hidden",
          willChange: "transform",
        }}
      >
        {/* Gradient scroll progress line */}
        <div
          ref={progressRef}
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            height: 2,
            width: 0,
            background: "linear-gradient(90deg, #159895, #00C9A7)",
            transition: "width 0.05s linear",
            zIndex: 1,
          }}
        />

        {/* Logo */}
        <div ref={logoRef} style={{ visibility: "hidden" }}>
          <Typography
            fontFamily="Pacifico"
            fontSize={26}
            sx={{
              cursor: "pointer",
              letterSpacing: "0.01em",
              "&:hover span": { textShadow: "0 0 20px #15989588" },
            }}
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          >
            Resume.
            <span style={{ color: "#159895", transition: "text-shadow 0.3s" }}>io</span>
          </Typography>
        </div>

        {/* Desktop nav links */}
        {smallScreen && (
          <div
            ref={linksRef}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              visibility: "hidden",
            }}
          >
            {NAV_ITEMS.map(({ label, id }) => {
              const isActive = activeSection === id;
              return (
                <div
                  key={id}
                  style={{ position: "relative", padding: "6px 16px", cursor: "pointer" }}
                  onClick={() => scrollTo(id)}
                >
                  <Typography
                    fontFamily="Poppins"
                    fontSize={11}
                    fontWeight={isActive ? 600 : 400}
                    sx={{
                      letterSpacing: "0.12em",
                      textTransform: "uppercase",
                      color: isActive ? "#159895" : "inherit",
                      opacity: isActive ? 1 : 0.55,
                      transition: "all 0.25s ease",
                      userSelect: "none",
                    }}
                  >
                    {label}
                  </Typography>

                  {/* Sliding underline indicator */}
                  {isActive && (
                    <motion.div
                      layoutId="nav-indicator"
                      style={{
                        position: "absolute",
                        bottom: 0,
                        left: 6,
                        right: 6,
                        height: 2,
                        borderRadius: 2,
                        background: "linear-gradient(90deg, #159895, #00C9A7)",
                      }}
                      transition={{ type: "spring", stiffness: 420, damping: 34 }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Right: Hire Me + Dark Mode + Mobile menu */}
        <div
          ref={ctaRef}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            visibility: "hidden",
          }}
        >
          {smallScreen && (
            <Button
              variant="contained"
              size="small"
              onClick={() => setOpenModal(true)}
              sx={{
                borderRadius: "20px",
                fontSize: 11,
                px: 2.5,
                py: 0.85,
                fontFamily: "Poppins",
                fontWeight: 600,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                background: "linear-gradient(135deg, #159895, #00C9A7)",
                boxShadow: "0 2px 18px rgba(21,152,149,0.35)",
                "&:hover": {
                  boxShadow: "0 4px 28px rgba(21,152,149,0.55)",
                  transform: "translateY(-1px)",
                },
                transition: "all 0.25s ease",
              }}
            >
              Hire Me
            </Button>
          )}

          <FormControlLabel
            control={
              <Switch
                checked={darkMode}
                onChange={(e) => setDarkMode(e.target.checked)}
                size="small"
              />
            }
            label=""
            sx={{ m: 0 }}
          />

          {!smallScreen && (
            <IconButton onClick={() => setOpenDrawer(true)} size="small">
              <MenuIcon width={20} height={20} />
            </IconButton>
          )}
        </div>
      </Grid>

      {/* ── Hire Me Modal ─────────────────────────────────────────────────── */}
      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <Grid
          container
          height="100vh"
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <Grid
            item
            container
            style={{ background: "#fff", borderRadius: 10, overflow: "auto" }}
            height="50%"
            sx={{
              position: { xs: "absolute", md: "unset" },
              width: { xs: "100%", md: "50%" },
              padding: { xs: "20px", md: "40px" },
              bottom: { xs: 0, md: "unset" },
            }}
            p={5}
          >
            <Grid
              item
              xs={12}
              display="flex"
              justifyContent="space-between"
              alignItems="flex-start"
            >
              <Typography fontFamily="Alkatra" fontWeight={600} fontSize={25}>
                Hire Me
              </Typography>
              <IconButton
                style={{ height: 20, width: 20, fontWeight: 600 }}
                onClick={() => setOpenModal(false)}
              >
                x
              </IconButton>
            </Grid>
            <Grid
              item
              xs={12}
              display="flex"
              flexWrap="wrap"
              gap={2}
              alignItems="center"
              justifyContent="center"
            >
              <Button href="mailto:patel.utsav@icloud.com">
                <Card>
                  <Typography fontSize={16} fontWeight={600} textAlign="center"
                    style={{ display: "flex", alignItems: "center", flexDirection: "column", justifyContent: "center" }}>
                    <MailIcon height={50} width={50} color="#002b5b" />
                    Drop A Mail
                  </Typography>
                </Card>
              </Button>
              <Button href="tel:+917874742111">
                <Card>
                  <Typography fontSize={16} fontWeight={600} textAlign="center"
                    style={{ display: "flex", alignItems: "center", flexDirection: "column", justifyContent: "center" }}>
                    <PhoneIcon height={50} width={50} color="#002b5b" />
                    Make A Call
                  </Typography>
                </Card>
              </Button>
              <Button href="https://www.linkedin.com/in/utsavoo7/" target="_blank">
                <Card>
                  <Typography fontSize={16} fontWeight={600} textAlign="center"
                    style={{ display: "flex", alignItems: "center", flexDirection: "column", justifyContent: "center" }}>
                    <LinkedIcon height={50} width={50} color="#002b5b" />
                    Explore Profile
                  </Typography>
                </Card>
              </Button>
              <Button href="https://www.instagram.com/_.utsav._/" target="_blank">
                <Card>
                  <Typography fontSize={16} fontWeight={600} textAlign="center"
                    style={{ display: "flex", alignItems: "center", flexDirection: "column", justifyContent: "center" }}>
                    <InstaIcon height={50} width={50} color="#002b5b" />
                    Get Socialized
                  </Typography>
                </Card>
              </Button>
            </Grid>
            <Grid item xs={12} />
          </Grid>
        </Grid>
      </Modal>

      {/* ── Mobile Drawer ─────────────────────────────────────────────────── */}
      <Drawer open={openDrawer} anchor="bottom" onClose={() => setOpenDrawer(false)}>
        <Grid item display="flex" flexDirection="column" alignItems="center" gap="10px" py={2}>
          {NAV_ITEMS.map(({ label, id }) => (
            <Button
              key={id}
              style={{ margin: "6px 0", fontFamily: "Poppins", letterSpacing: "0.1em", fontSize: 13 }}
              onClick={() => { scrollTo(id); setOpenDrawer(false); }}
            >
              {label}
            </Button>
          ))}
          <Button
            variant="contained"
            onClick={() => { setOpenModal(true); setOpenDrawer(false); }}
            sx={{
              borderRadius: "20px",
              fontFamily: "Poppins",
              fontWeight: 600,
              letterSpacing: "0.08em",
              mt: 1,
              background: "linear-gradient(135deg, #159895, #00C9A7)",
            }}
          >
            Hire Me
          </Button>
        </Grid>
      </Drawer>
    </>
  );
};

export default Navbar;
